import React, { useRef, useCallback, useMemo, useState, useEffect } from "react";
import {
  Stack,
  Group,
  TextInput,
  Text,
  Button,
  Input,
  Popover,
  ScrollArea,
  Box,
  UnstyledButton,
} from "@mantine/core";
import { IconPlus, IconSearch } from "@tabler/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../richTextEditor.css";
import { MCA_NOTIFICATION_TEMPLATE_VARS } from "../../data/mcaEntities";
import FormReadOnlyField from "../formReadOnlyField";

const insertToken = (inputEl, value, token, onChange) => {
  const current = value ?? "";
  if (!inputEl || inputEl.selectionStart == null) {
    onChange(current + token);
    return;
  }
  const start = inputEl.selectionStart;
  const end = inputEl.selectionEnd ?? start;
  const next = current.slice(0, start) + token + current.slice(end);
  onChange(next);
  requestAnimationFrame(() => {
    const pos = start + token.length;
    inputEl.setSelectionRange(pos, pos);
    inputEl.focus();
  });
};

const insertTokenQuill = (quillRef, token) => {
  const quill = quillRef.current?.getEditor?.();
  if (!quill) return;
  const range = quill.getSelection(true);
  const index = range ? range.index : Math.max(0, quill.getLength() - 1);
  if (token === "{{entityUrl}}" || token === "{{entityLink}}") {
    quill.insertText(index, "{{entity.code}}", "link", token);
    quill.setSelection(index + "{{entity.code}}".length);
    return;
  }
  quill.insertText(index, token);
  quill.setSelection(index + token.length);
};

const toEditorHtml = (value) => {
  if (!value) return "";
  if (/<[a-z][\s\S]*>/i.test(value)) return value;
  return value
    .split("\n")
    .map((line) => `<p>${line || "<br>"}</p>`)
    .join("");
};

const BODY_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

const BODY_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
];

const BODY_MODULES = { toolbar: BODY_TOOLBAR };

const InsertFieldMenu = ({ onInsert, variables = MCA_NOTIFICATION_TEMPLATE_VARS }) => {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!opened) setSearch("");
  }, [opened]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return variables;
    return variables.filter(
      (item) =>
        item.label?.toLowerCase().includes(query) ||
        item.token?.toLowerCase().includes(query) ||
        item.value?.toLowerCase().includes(query)
    );
  }, [variables, search]);

  const handleSelect = (token) => {
    onInsert(token);
    setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={360}
      withinPortal
      trapFocus
    >
      <Popover.Target>
        <Button
          size="xs"
          variant="light"
          leftIcon={<IconPlus size={14} />}
          onClick={() => setOpened((o) => !o)}
        >
          Insert field
        </Button>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Box
          p="xs"
          sx={(theme) => ({
            borderBottom: `1px solid ${
              theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[3]
            }`,
          })}
          onMouseDown={(e) => e.preventDefault()}
        >
          <TextInput
            size="xs"
            placeholder="Search fields..."
            icon={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            autoFocus
          />
        </Box>
        <ScrollArea.Autosize mah={320} type="auto" offsetScrollbars>
          {filtered.length ? (
            filtered.map((item) => (
              <UnstyledButton
                key={item.value}
                onClick={() => handleSelect(item.token)}
                sx={(theme) => ({
                  display: "block",
                  width: "100%",
                  padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
                  textAlign: "left",
                  "&:hover": {
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[6]
                        : theme.colors.gray[0],
                  },
                })}
              >
                <Text size="sm">
                  {item.label}{" "}
                  <Text component="span" color="dimmed" size="xs">
                    {item.token}
                  </Text>
                </Text>
              </UnstyledButton>
            ))
          ) : (
            <Text size="sm" color="dimmed" p="sm">
              No matching fields
            </Text>
          )}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
};

const McaNotificationTemplateInput = ({
  label,
  required,
  multiline = false,
  value,
  onChange,
  placeholder,
  minRows,
  variables,
  readOnly = false,
}) => {
  const inputRef = useRef(null);
  const quillRef = useRef(null);
  const editorMinRows = minRows ?? (multiline ? 12 : 4);

  const handleInsert = (token) => {
    if (!token) return;
    if (multiline) {
      insertTokenQuill(quillRef, token);
      return;
    }
    insertToken(inputRef.current, value, token, onChange);
  };

  const setQuillRef = useCallback((el) => {
    quillRef.current = el;
  }, []);

  const handleEditorChange = useCallback(
    (content) => {
      onChange(content);
    },
    [onChange],
  );

  if (readOnly) {
    if (multiline && value && /<[a-z][\s\S]*>/i.test(value)) {
      return (
        <div>
          {label ? (
            <Text size="xs" color="dimmed">
              {label}
            </Text>
          ) : null}
          <div className="ql-snow">
            <div
              className="ql-editor"
              style={{ padding: 0 }}
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </div>
        </div>
      );
    }
    return (
      <FormReadOnlyField
        label={label}
        value={value || "-"}
        preLine={multiline}
      />
    );
  }

  const field = multiline ? (
    <Stack spacing="xs" sx={{ flex: 1 }}>
      <Group position="right">
        <InsertFieldMenu onInsert={handleInsert} variables={variables} />
      </Group>
      <div
        className="rich-text-editor-sized"
        style={{ "--rte-min-rows": editorMinRows }}
      >
        <ReactQuill
          ref={setQuillRef}
          theme="snow"
          className="black-theme"
          value={toEditorHtml(value)}
          onChange={handleEditorChange}
          placeholder={placeholder}
          formats={BODY_FORMATS}
          modules={BODY_MODULES}
        />
      </div>
    </Stack>
  ) : (
    <Stack spacing="xs" sx={{ width: "100%" }}>
      <Group position="right">
        <InsertFieldMenu onInsert={handleInsert} variables={variables} />
      </Group>
      <TextInput
        ref={inputRef}
        required={required}
        size="sm"
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        sx={{ width: "100%" }}
      />
    </Stack>
  );

  return (
    <Input.Wrapper label={label} required={required} size="sm" sx={{ width: "100%" }}>
      {field}
    </Input.Wrapper>
  );
};

export default McaNotificationTemplateInput;
